import React, { useState, useEffect, useMemo } from 'react';
import { Grid, Paper, FormControl, InputLabel, Select, MenuItem, Button } from '@material-ui/core';
import { useDispatch, useSelector } from 'react-redux';
import { getAvailableFiscalQuarters } from '../redux/management.thunk';
import { selectAvailableFiscalQuarters, selectAvailableCurrentFiscalQuarter } from '../redux/management.slice';
import { Skeleton } from '@mui/material';

const UpdateFiscalQuarterFilter = ({ touched, setTouched, fetchData, type }) => {
  const [selected, setSelected] = useState([]);
  const dispatch = useDispatch();
  const availableFiscalQuarters = useSelector(selectAvailableFiscalQuarters);
  const availableCurrentFiscalQuarter = useSelector(selectAvailableCurrentFiscalQuarter);

  // Sort quarters in descending order (most recent first)
  const sortedQuarters = useMemo(() => {
    if (!availableFiscalQuarters || !Array.isArray(availableFiscalQuarters)) return [];

    try {
      return [...availableFiscalQuarters].sort((a, b) => {
        if (!a || !b) return 0;
        const [yearA, quarterA] = a.split('-Q').map(Number);
        const [yearB, quarterB] = b.split('-Q').map(Number);
        if (yearA !== yearB) return yearB - yearA;
        return quarterB - quarterA;
      });
    } catch (error) {
      console.error('Error sorting quarters:', error);
      return availableFiscalQuarters;
    }
  }, [availableFiscalQuarters]);

  useEffect(() => {
    if (type) {
      dispatch(getAvailableFiscalQuarters({ type }));
    }
  }, [dispatch, type]);

  useEffect(() => {
    if (sortedQuarters && sortedQuarters.length > 0 && selected.length === 0) {
      // Default to the current fiscal quarter if available, otherwise the most recent quarter
      const defaultQuarter = availableCurrentFiscalQuarter && sortedQuarters.includes(availableCurrentFiscalQuarter)
        ? availableCurrentFiscalQuarter
        : sortedQuarters[0];
      setSelected([defaultQuarter]);
      dispatch(fetchData({ filters: [{ name: "Fiscal_Quarter", value: [defaultQuarter] }] }));
    }
  }, [sortedQuarters, availableCurrentFiscalQuarter, dispatch, selected, fetchData]);

  const handleFilterChange = (event) => {
    const { value } = event.target;

    // If no values selected, default to "All"
    if (value.length === 0) {
      setSelected(["All"]);
      return;
    }

    // If "All" was previously selected and user is now selecting specific quarters
    if (selected.includes("All") && value.length > 1) {
      // Remove "All" and keep only the newly selected quarters
      const newValue = value.filter((v) => v !== "All");
      setSelected(newValue);
      return;
    }

    // If user selects "All" along with other quarters, only keep "All"
    if (value.includes("All") && value.length > 1) {
      setSelected(["All"]);
      return;
    }

    // Otherwise, just set the selected values
    setSelected(value);
  };

  const handleGo = () => {
    const filters = selected.includes("All")
      ? [{ name: "Fiscal_Quarter", value: [] }]
      : [{ name: "Fiscal_Quarter", value: selected }];

    if (touched) {
      if (window.confirm("You have unsaved changes. Do you want to discard and reload?")) {
        dispatch(fetchData({ filters }));
        if (setTouched) setTouched(false);
      }
    } else {
      dispatch(fetchData({ filters }));
    }
  };

  return (
    <>
      {sortedQuarters && sortedQuarters.length > 0 ? (
        <Grid container item alignItems="center" spacing={2}>
          <Grid item xs={12} sm={4} md={3} lg={2} style={{ padding: '1rem' }}>
            <FormControl fullWidth component={Paper} style={{ padding: '1rem' }}>
              <InputLabel style={{ padding: '1rem' }}>Fiscal Quarter:</InputLabel>
              <Select
                value={selected}
                onChange={handleFilterChange}
                multiple
              >
                <MenuItem value={"All"}>All</MenuItem>
                {sortedQuarters.map((quarter) => (
                  <MenuItem key={quarter} value={quarter}>{quarter}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item>
            <Button onClick={handleGo} color="primary" variant="contained">
              GO
            </Button>
          </Grid>
        </Grid>
      ) : (
        <Skeleton width={"95%"} height={70} />
      )}
    </>
  );
};

export default UpdateFiscalQuarterFilter;
